import { useCallback, useRef } from 'react';
import { Platform, type SectionListScrollParams } from 'react-native';

import { useAnchorSectionList } from './context';

export function useScrollToSection() {
  const context = useAnchorSectionList();
  const intervalIdRef = useRef<ReturnType<typeof setInterval>>();
  return useCallback(
    (
      sectionId: string,
      opts?: Pick<SectionListScrollParams, 'animated' | 'viewOffset' | 'viewPosition'>
    ) => {
      const sectionIndex = context.flashListData.findIndex(
        datum => datum.type === 'section' && datum.section.id === sectionId
      );

      if (sectionIndex < 0) {
        return;
      }

      context.flashListRef.current?.scrollToIndex({ ...opts, index: sectionIndex });

      // Handle focus for web
      if (Platform.OS === 'web') {
        clearInterval(intervalIdRef.current);

        let pollingDuration = 0;
        const INTERVAL_DURATION = 100;
        const MAX_TIME_BEFORE_GIVE_UP = 5000;

        // We have to check on an interval because sections are virtualized
        // the section that we want to focus might not exist in the dom.
        // we should be actively scrolling towards the section so it should
        // eventually be rendered into dom.
        intervalIdRef.current = setInterval(() => {
          pollingDuration += INTERVAL_DURATION;

          if (pollingDuration > MAX_TIME_BEFORE_GIVE_UP) {
            // we have been poling longer than we want... give up :<
            clearInterval(intervalIdRef.current);
            return;
          }

          const sectionEl = document.getElementById(sectionId);

          if (!sectionEl) {
            // haven't found section id, wait until next interval to try again.
            return;
          }

          sectionEl.focus({ preventScroll: true });
          clearInterval(intervalIdRef.current);
        }, INTERVAL_DURATION);
      }
    },
    [context.flashListData, context.flashListRef]
  );
}
